/**
 * Shared structured Conditions editor used by both:
 *   • LOS Document drilldown drawer (loanProcessing/DocumentDrilldownDrawer)
 *   • Task List Manager drawer       (taskLists/TaskDrilldownDrawer)
 *
 * Both surfaces persist to the same Mongo shape — the LOS consumes them
 * indistinguishably — so the UI must also be identical.
 */
import { useRef, useState } from "react";
import { Box } from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineOutlined from "@mui/icons-material/DeleteOutlineOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";

const REVIEW_LEVEL_HINTS = ["UW", "Credit Committee", "Closer", "Post-Close"];
const RISK_HINTS = ["Low", "Medium", "High", "Compensating"];
const LOAN_PURPOSE_HINTS = [
  "Purchase",
  "Refinance",
  "Cash-Out Refinance",
  "Construction",
  "Bridge",
];

// Shared native form-control styling (translated from Tailwind utility classes).
const inputBaseSx = {
  width: "100%",
  px: "10px",
  py: "6px",
  backgroundColor: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  fontSize: 14,
  color: "#0f172a",
  boxSizing: "border-box",
  font: "inherit",
  "&:focus": {
    outline: "none",
    boxShadow: "0 0 0 2px rgba(59,130,246,0.4)",
  },
};

const emeraldInputSx = {
  ...inputBaseSx,
  "&:focus": {
    outline: "none",
    boxShadow: "0 0 0 2px rgba(16,185,129,0.4)",
  },
};

export function ConditionsEditor({
  conditions,
  setConditions,
  productSuggestions = [],
  purposeSuggestions = [],
  emptyLabel,
}) {
  const blank = () => ({
    conditionName: "",
    conditionStandardText: "",
    compensatingFactors: [],
    reviewLevel: "",
    conditionRisk: "",
    guidelineReference: "",
    loanProductTypes: [],
    loanPurposes: [],
    conditionTrigger: "",
  });

  const update = (idx, patch) =>
    setConditions(conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
      data-testid="conditions-editor"
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        <Box
          component="button"
          onClick={() => setConditions([...conditions, blank()])}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            px: "10px",
            py: "4px",
            fontSize: 11,
            backgroundColor: "#eff6ff",
            color: "#1d4ed8",
            borderRadius: "4px",
            border: "1px solid #bfdbfe",
            cursor: "pointer",
            transition: "background-color 0.15s",
            "&:hover": { backgroundColor: "#dbeafe" },
          }}
        >
          <AddRounded sx={{ fontSize: 11 }} /> Add condition
        </Box>
      </Box>

      {conditions.length === 0 ? (
        <Box
          component="p"
          sx={{ fontSize: 12, color: "#64748b", fontStyle: "italic", m: 0 }}
        >
          {emptyLabel || "No conditions defined yet."}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {conditions.map((c, i) => (
            <ConditionCard
              key={i}
              index={i}
              condition={c}
              productSuggestions={productSuggestions}
              purposeSuggestions={purposeSuggestions}
              onChange={(patch) => update(i, patch)}
              onRemove={() => setConditions(conditions.filter((_, j) => j !== i))}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function ConditionCard({
  index,
  condition,
  productSuggestions,
  purposeSuggestions,
  onChange,
  onRemove,
}) {
  const guidelineRef = useRef(null);
  const [factorDraft, setFactorDraft] = useState("");

  const addFactor = (raw) => {
    const v = raw.trim();
    if (!v) return;
    if (condition.compensatingFactors.includes(v)) return;
    onChange({ compensatingFactors: [...condition.compensatingFactors, v] });
    setFactorDraft("");
  };
  const removeFactor = (v) =>
    onChange({
      compensatingFactors: condition.compensatingFactors.filter((x) => x !== v),
    });

  const copyGuideline = async () => {
    if (!condition.guidelineReference) return;
    try {
      await navigator.clipboard.writeText(condition.guidelineReference);
    } catch {
      guidelineRef.current?.select();
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f9fafb",
        border: "1px solid #d1d5db",
        borderRadius: "8px",
        p: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
      data-testid="condition-card"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
        }}
      >
        <Box component="span" sx={{ fontSize: 11, color: "#64748b" }}>
          Condition #{index + 1}
        </Box>
        <Box
          component="button"
          onClick={onRemove}
          title="Remove condition"
          sx={{
            p: "4px",
            color: "#64748b",
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "inline-flex",
            transition: "color 0.15s",
            "&:hover": { color: "#dc2626" },
          }}
        >
          <DeleteOutlineOutlined sx={{ fontSize: 12 }} />
        </Box>
      </Box>

      <Field label="Name">
        <Box
          component="input"
          value={condition.conditionName}
          onChange={(e) => onChange({ conditionName: e.target.value })}
          placeholder="e.g. Updated bank statement required"
          sx={inputBaseSx}
        />
      </Field>

      <Field label="Standard text">
        <Box
          component="textarea"
          value={condition.conditionStandardText}
          onChange={(e) => onChange({ conditionStandardText: e.target.value })}
          rows={2}
          placeholder="Instructions to provide on standard conditions for LOS to describe the issue and how to cure"
          sx={{ ...inputBaseSx, resize: "vertical" }}
        />
      </Field>

      <Field label="Condition trigger (rule-code seed)">
        <Box
          component="textarea"
          value={condition.conditionTrigger || ""}
          onChange={(e) => onChange({ conditionTrigger: e.target.value })}
          rows={3}
          placeholder={
            'e.g. "When loan_purpose == DSCR and appraisal.condition_rating worse than C4, raise this condition." Plain language now; gets compiled into LOS rule code later.'
          }
          spellCheck={false}
          sx={{
            ...inputBaseSx,
            fontFamily: "monospace",
            lineHeight: 1.625,
            resize: "vertical",
          }}
        />
      </Field>

      <Field label="Compensating factors">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "6px" }}>
          {condition.compensatingFactors.map((v) => (
            <Box
              key={v}
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                px: "8px",
                py: "2px",
                borderRadius: 999,
                fontSize: 11,
                backgroundColor: "#ecfdf5",
                color: "#065f46",
                border: "1px solid #a7f3d0",
              }}
            >
              {v}
              <Box
                component="button"
                onClick={() => removeFactor(v)}
                sx={{
                  backgroundColor: "transparent",
                  border: "none",
                  p: 0,
                  cursor: "pointer",
                  display: "inline-flex",
                  color: "inherit",
                  "&:hover": { color: "#0f172a" },
                }}
              >
                <CloseRounded sx={{ fontSize: 10 }} />
              </Box>
            </Box>
          ))}
        </Box>
        <Box
          component="input"
          value={factorDraft}
          onChange={(e) => setFactorDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addFactor(factorDraft);
            }
          }}
          placeholder="Add factor (Enter to commit)"
          sx={emeraldInputSx}
        />
      </Field>

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
        <Field label="Review level">
          <Box
            component="input"
            value={condition.reviewLevel}
            onChange={(e) => onChange({ reviewLevel: e.target.value })}
            list={`review-levels-${index}`}
            placeholder="e.g. UW"
            sx={inputBaseSx}
          />
          <datalist id={`review-levels-${index}`}>
            {REVIEW_LEVEL_HINTS.map((h) => (
              <option key={h} value={h} />
            ))}
          </datalist>
        </Field>
        <Field label="Condition risk">
          <Box
            component="input"
            value={condition.conditionRisk}
            onChange={(e) => onChange({ conditionRisk: e.target.value })}
            list={`risks-${index}`}
            placeholder="e.g. Medium"
            sx={inputBaseSx}
          />
          <datalist id={`risks-${index}`}>
            {RISK_HINTS.map((h) => (
              <option key={h} value={h} />
            ))}
          </datalist>
        </Field>
      </Box>

      <Field label="Loan product types (empty = applies to all)">
        <PillRow
          values={condition.loanProductTypes || []}
          onChange={(v) => onChange({ loanProductTypes: v })}
          placeholder="Add product (Enter)"
          options={productSuggestions}
          accent="blue"
        />
      </Field>

      <Field label="Loan purposes (empty = applies to all)">
        <PillRow
          values={condition.loanPurposes || []}
          onChange={(v) => onChange({ loanPurposes: v })}
          placeholder="Add purpose (Enter)"
          options={purposeSuggestions.length ? purposeSuggestions : LOAN_PURPOSE_HINTS}
          accent="violet"
        />
      </Field>

      <Field
        label="Guideline reference"
        rightAction={
          <Box
            component="button"
            onClick={copyGuideline}
            title="Copy"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: 11,
              color: "#64748b",
              backgroundColor: "transparent",
              border: "none",
              p: 0,
              cursor: "pointer",
              transition: "color 0.15s",
              "&:hover": { color: "#0f172a" },
            }}
          >
            <ContentCopyOutlined sx={{ fontSize: 11 }} /> Copy
          </Box>
        }
      >
        <Box
          component="textarea"
          ref={guidelineRef}
          value={condition.guidelineReference}
          onChange={(e) => onChange({ guidelineReference: e.target.value })}
          rows={4}
          placeholder="Paste guideline text here. Multi-line + paste-formatting preserved."
          sx={{
            ...inputBaseSx,
            resize: "vertical",
            fontFamily: "monospace",
            lineHeight: 1.625,
          }}
        />
      </Field>
    </Box>
  );
}

function Field({ label, rightAction, children }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <Box
          component="p"
          sx={{
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "#64748b",
            m: 0,
          }}
        >
          {label}
        </Box>
        {rightAction}
      </Box>
      {children}
    </Box>
  );
}

function PillRow({ values, onChange, placeholder, options, accent = "blue" }) {
  const [draft, setDraft] = useState("");

  const accentSx =
    accent === "violet"
      ? { backgroundColor: "#f5f3ff", color: "#5b21b6", border: "1px solid #ddd6fe" }
      : accent === "emerald"
      ? { backgroundColor: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" }
      : { backgroundColor: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" };

  const focusRing =
    accent === "violet"
      ? "0 0 0 2px rgba(139,92,246,0.4)"
      : accent === "emerald"
      ? "0 0 0 2px rgba(16,185,129,0.4)"
      : "0 0 0 2px rgba(59,130,246,0.4)";

  const add = (raw) => {
    const v = raw.trim();
    if (!v || values.includes(v)) return;
    onChange([...values, v]);
    setDraft("");
  };

  const allOptions = options || [];
  const allSelected =
    allOptions.length > 0 && allOptions.every((o) => values.includes(o));

  const suggestions = allOptions
    .filter((o) => !values.includes(o) && o.toLowerCase().includes(draft.toLowerCase()))
    .slice(0, 6);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      {(values.length > 0 || allOptions.length > 0) && (
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <Box
            component="span"
            sx={{
              fontSize: 10,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: "#64748b",
            }}
          >
            {values.length} selected
          </Box>
          {allOptions.length > 0 && (
            <Box
              component="button"
              onClick={() =>
                onChange(allSelected ? [] : Array.from(new Set([...values, ...allOptions])))
              }
              sx={{
                fontSize: 10,
                px: "6px",
                py: "2px",
                borderRadius: "4px",
                color: "#334155",
                backgroundColor: "transparent",
                border: "none",
                cursor: "pointer",
                transition: "background-color 0.15s, color 0.15s",
                "&:hover": { color: "#0f172a", backgroundColor: "#e5e7eb" },
              }}
            >
              {allSelected ? "Clear all" : "Select all"}
            </Box>
          )}
        </Box>
      )}
      {values.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {values.map((v) => (
            <Box
              key={v}
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                px: "8px",
                py: "2px",
                borderRadius: 999,
                fontSize: 11,
                ...accentSx,
              }}
            >
              {v}
              <Box
                component="button"
                onClick={() => onChange(values.filter((x) => x !== v))}
                aria-label={`Remove ${v}`}
                sx={{
                  backgroundColor: "transparent",
                  border: "none",
                  p: 0,
                  cursor: "pointer",
                  display: "inline-flex",
                  color: "inherit",
                  "&:hover": { color: "#0f172a" },
                }}
              >
                <CloseRounded sx={{ fontSize: 10 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}
      <Box
        component="input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            add(draft);
          }
        }}
        placeholder={placeholder}
        sx={{
          ...inputBaseSx,
          "&:focus": { outline: "none", boxShadow: focusRing },
        }}
      />
      {suggestions.length > 0 && draft.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {suggestions.map((s) => (
            <Box
              component="button"
              key={s}
              onClick={() => add(s)}
              sx={{
                px: "8px",
                py: "2px",
                fontSize: 10,
                color: "#64748b",
                backgroundColor: "#f3f4f6",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                transition: "background-color 0.15s, color 0.15s",
                "&:hover": { color: "#0f172a", backgroundColor: "#e5e7eb" },
              }}
            >
              + {s}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default ConditionsEditor;
